import { Logger } from '~/plugins/log'
const logger = new Logger()
logger.setContext('Review')

const getDefaultState = () => ({
  summary: {},
  reviews: {},
  reviewCount: null,
  pageSize: 10,
  pagesLoaded: [],
  productSummary: {},
  productReviews: {},
  productReviewsPageSize: 5,
  productReviewsPagesLoaded: {},
  summaryLastUpdate: null,
  productSummariesLastUpdate: null
})

export const state = getDefaultState

export const mutations = {
  RESET (state) {
    Object.assign(state, getDefaultState())
  },
  SET_SUMMARY (state, summary) {
    state.summary = summary
    state.summaryLastUpdate = (new Date()).valueOf()
  },
  SET_PRODUCT_SUMMARY (state, { data, baseSKU }) {
    state.productSummary = { ...state.productSummary, [baseSKU]: data }
  },
  SET_PRODUCT_REVIEWS (state, { data, baseSKU }) {
    let reviews = state.productReviews[baseSKU] || {}
    const existingIds = Object.values(reviews).map(review => review.id)
    reviews = {
      ...reviews,
      ...Object.fromEntries(data.reviews.filter(
        review => !existingIds.includes(review.id)
      ).map(
        (review, idx) => {
          review.items = review.items.filter(
            item => item.startsWith(baseSKU)
          )
          return [data.offset + idx, review]
        }
      ))
    }

    state.productReviews = {
      ...state.productReviews,
      [baseSKU]: reviews
    }
  },
  SET_PRODUCT_REVIEWS_PAGES_LOADED (state, { baseSKU, page }) {
    let pagesLoaded = state.productReviewsPagesLoaded[baseSKU] || []
    pagesLoaded = [...new Set([...pagesLoaded, page])]

    state.productReviewsPagesLoaded = {
      ...state.productReviewsPagesLoaded,
      [baseSKU]: pagesLoaded
    }
  },
  SET_REVIEWS (state, { data }) {
    let reviews = state.reviews || {}
    const existingIds = Object.values(reviews).map(review => review.id)
    reviews = {
      ...reviews,
      ...Object.fromEntries(data.reviews.filter(
        review => !existingIds.includes(review.id)
      ).map((review, idx) => [data.offset + idx, review]))
    }

    state.reviews = reviews
    state.reviewCount = data.count
  },
  SET_REVIEWS_PAGES_LOADED (state, { page }) {
    state.pagesLoaded = [...new Set([...state.pagesLoaded, page])]
  }
}

export const getters = {
  summary: state => state.summary,
  count: state => state.summary.count,
  rating: state => state.summary.rating,
  recommendRate: state => state.summary.recommendRate,
  productSummary: state => baseSKU => state.productSummary[baseSKU] || {},
  productCount: state => baseSKU => (state.productSummary[baseSKU] || {}).count,
  productRating: state => baseSKU => (state.productSummary[baseSKU] || {}).rating,
  productRecommendRate: state => baseSKU => (state.productSummary[baseSKU] || {}).recommendRate,
  productReviews: state => (baseSKU, page) => {
    const offset = (page - 1) * state.productReviewsPageSize
    return Object.entries((state.productReviews[baseSKU] || [])).filter(
      ([idx, _]) => idx >= offset && idx < offset + state.productReviewsPageSize
    ).map(([_, review]) => review)
  },
  productReviewPages: state => baseSKU => Math.ceil((state.productSummary[baseSKU] || {}).count / state.productReviewsPageSize),
  productReviewPagesLoaded: state => baseSKU => state.productReviewsPagesLoaded[baseSKU] || [],
  reviews: state => (page) => {
    const offset = (page - 1) * state.pageSize
    return Object.entries((state.reviews || [])).filter(
      ([idx, _]) => idx >= offset && idx < offset + state.pageSize
    ).map(([_, review]) => review)
  },
  reviewPages: state => Math.ceil(state.reviewCount / state.pageSize),
  pagesLoaded: state => state.pagesLoaded,
  pageNumbers: () => (page, pages, neighbours) => {
    let pageNumbers = []
    for (let i = 1; i <= neighbours; i++) {
      pageNumbers.push(i)
    }
    for (let i = page - neighbours; i <= page + neighbours; i++) {
      pageNumbers.push(i)
    }
    for (let i = pages - neighbours + 1; i <= pages; i++) {
      pageNumbers.push(i)
    }
    pageNumbers = [...new Set(pageNumbers)].sort(
      (a, b) => a - b
    ).filter(
      pageNumber => pageNumber >= 1 & pageNumber <= pages
    )

    const pageNumbersWithElipses = ['<']
    let previousPageNumber = 1
    pageNumbers.forEach((pageNumber) => {
      if (pageNumber > previousPageNumber + 1) {
        pageNumbersWithElipses.push('...')
      }
      pageNumbersWithElipses.push(pageNumber)
      previousPageNumber = pageNumber
    })
    pageNumbersWithElipses.push('>')

    return pageNumbersWithElipses
  }
}

export const actions = {
  async GET_SUMMARY ({ state, commit }) {
    // Persist data for 20 minutes
    if (state.summaryLastUpdate && state.summaryLastUpdate > (new Date()).valueOf() - 1000 * 60 * 20) {
      return
    }

    const { data } = await this.$publicAPI.get('reviews/summary/', { useCache: true })
    commit('SET_SUMMARY', data)
  },

  async GET_PRODUCT_SUMMARY ({ state, commit }, { baseSKU, quiet = false }) {
    if (baseSKU in state.productSummary) return

    const getMethod = quiet ? this.$publicAPI.quietGet : this.$publicAPI.get
    try {
      const { data } = await getMethod(`reviews/summary/${baseSKU}/`, { useCache: true })
      commit('SET_PRODUCT_SUMMARY', { data, baseSKU })
    } catch (error) {
      this.app.$sentry.captureException(error)
      logger.error(error)
    }
  },

  async GET_PRODUCT_REVIEWS ({ commit, state, getters }, { baseSKU, page, quiet = false, generic = false }) {
    // Bail early if we've already got reviews for this page
    if (getters.productReviewPagesLoaded(baseSKU).includes(page)) return

    const offset = (page - 1) * state.productReviewsPageSize

    const getMethod = quiet ? this.$publicAPI.quietGet : this.$publicAPI.get
    try {
      const { data } = await getMethod('reviews/', { params: { product: baseSKU, offset, limit: state.productReviewsPageSize, generic }, useCache: true })
      commit('SET_PRODUCT_REVIEWS', { data, baseSKU })
      commit('SET_PRODUCT_REVIEWS_PAGES_LOADED', { baseSKU, page })
    } catch (error) {
      this.app.$sentry.captureException(error)
      logger.error(error)
    }
  },

  async GET_ALL_PRODUCT_SUMMARIES ({ state, commit }) {
    try {
      const { data } = await this.$publicAPI.get('reviews/all_product_summaries/', { useCache: true })
      Object.entries(data).forEach(
        ([product, response]) => commit('SET_PRODUCT_SUMMARY', { data: response, baseSKU: product })
      )
      return data
    } catch (error) {
      this.app.$sentry.captureException(error)
      logger.error(error)
    }
  },

  async GET_REVIEWS ({ commit, state }, { page }) {
    const offset = (page - 1) * state.pageSize
    try {
      const { data } = await this.$publicAPI.get('reviews/', { params: { offset, limit: state.pageSize }, useCache: true })
      commit('SET_REVIEWS', { data })
      commit('SET_REVIEWS_PAGES_LOADED', { page })
    } catch (error) {
      this.app.$sentry.captureException(error)
      logger.error(error)
    }
  }
}
